import PropTypes from "prop-types";
import { useState } from "react";
import { t } from "ttag";

import { useListUserRecipientsQuery } from "metabase/api";
import LoadingSpinner from "metabase/common/components/LoadingSpinner";
import ExportSpreadsheet from "metabase/jerry/Spreadsheet/ExportSpreadsheet";
import ScheduleSpreadsheet from "metabase/jerry/Spreadsheet/ScheduleSpreadsheet";
import ScheduleSpreadsheetHistory from "metabase/jerry/Spreadsheet/ScheduleSpreadsheetHistory";
import { PLUGIN_FEATURE_LEVEL_PERMISSIONS } from "metabase/plugins";
import { ActionIcon, Icon, Popover, Stack, Title, Tooltip } from "metabase/ui";

function SubscribeExportSheet({
  className,
  question,
  result,
  user,
  isShowingRawTable,
  rawSeries,
  visualizationSettings,
}) {
  const { data: users } = useListUserRecipientsQuery();
  const [status, setStatus] = useState(`idle`);

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);

  const icon = "google_sheet";
  const iconSize = 18;

  const LOADER_SCALE_FACTOR = 0.9;

  const renderIcon = ({ icon, status, iconSize }) => {
    if ([`idle`, `resolved`, `rejected`].includes(status)) {
      return (
        <Tooltip label={t`Subscribe Google Sheet Export`}>
          <Icon
            // style={{ marginBottom: "-3px" }}
            data-testid="download-button"
            title={t`Export this data`}
            name={icon}
            size={iconSize}
          />
        </Tooltip>
      );
    } else if (status === "pending") {
      return <LoadingSpinner size={iconSize * LOADER_SCALE_FACTOR} />;
    } else {
      throw new Error(`Unknown export status: ${status}`);
    }
  };

  const handlePopoverChange = (isOpen) => {
    if (isOpen) {
      setIsPopoverOpen(true);
    } else {
      if (!isScheduleModalOpen && !isHistoryModalOpen) {
        setIsPopoverOpen(false);
      }
    }
  };

  const triggerElement = (
    <Tooltip
      label={
        status === "pending" ? t`Exporting…` : t`Subscribe Google Sheet Export`
      }
    >
      <ActionIcon
        className={`${className} text-brand-hover`}
        onClick={() => {
          if (status !== "pending") {
            setIsPopoverOpen(!isPopoverOpen);
          }
        }}
        aria-label={t`Subscribe Google Sheet Export`}
        style={{ color: "var(--color-text-medium)" }}
      >
        {renderIcon({ icon, status, iconSize })}
      </ActionIcon>
    </Tooltip>
  );

  return (
    <Popover
      opened={isPopoverOpen}
      onChange={handlePopoverChange}
      disabled={status === "pending"}
    >
      <Popover.Target>{triggerElement}</Popover.Target>

      <Popover.Dropdown p="0.75rem">
        <Stack w={336} gap="md">
          <Title order={5}>{t`Subscribe Google Sheet Export`}</Title>
          <div>
            <>
              {user && (
                <ExportSpreadsheet
                  card={question.card()}
                  result={result}
                  user={user}
                  isShowingRawTable={isShowingRawTable}
                  rawSeries={rawSeries}
                  visualizationSettings={visualizationSettings}
                  onDownloadStart={() => {
                    setStatus("pending");
                    setIsPopoverOpen(false);
                  }}
                  onDownloadResolved={() => setStatus("resolved")}
                  onDownloadRejected={() => setStatus("rejected")}
                />
              )}
              {question.isSaved() && (
                <>
                  <ScheduleSpreadsheet
                    question={question}
                    user={user}
                    users={users?.data ?? []}
                    result={result}
                    isShowingRawTable={isShowingRawTable}
                    rawSeries={rawSeries}
                    visualizationSettings={visualizationSettings}
                    onModalStateChange={setIsScheduleModalOpen}
                  />
                  <ScheduleSpreadsheetHistory
                    question={question}
                    user={user}
                    users={users?.data ?? []}
                    result={result}
                    onModalStateChange={setIsHistoryModalOpen}
                  />
                </>
              )}
            </>
          </div>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}

SubscribeExportSheet.propTypes = {
  className: PropTypes.string,
  question: PropTypes.shape({
    card: PropTypes.func.isRequired,
    isSaved: PropTypes.func.isRequired,
  }).isRequired,
  result: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  isShowingRawTable: PropTypes.bool,
  rawSeries: PropTypes.array,
  visualizationSettings: PropTypes.object,
};

SubscribeExportSheet.shouldRender = ({ user, result }) => {
  return (
    result &&
    !result.error &&
    PLUGIN_FEATURE_LEVEL_PERMISSIONS.canDownloadResults(result)
  );
};

export default SubscribeExportSheet;
